import { html, View } from 'rune-ts';
import { CreatorProductDefaultData } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { FormDropdown } from '@services/MarppleShop/shared/components/cells/FormDropDown/FormDropdown.ts';
import { FORM_DROPDOWN_CONSTANT } from '@services/MarppleShop/shared/components/cells/FormDropDown/type.ts';
import klass from './Input.module.scss';
import base_klass from '../../../../../shared/component/atom/Input/core/input.module.scss';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { categoryCodeSchema } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/store_product.ts';
import { undefined, z } from 'zod';
import { ValueOf } from '../../../../../../../shared/type/util.type.ts';
import {
  controlErrorMessage,
  required_html,
} from '@services/MarppleShopStudio/shared/component/atom/Input/core/helper.ts';
import { CategoryServiceS } from '../../../../../../../shared/mps-shared/service/category-share.service.ts';
import { CategoryLevel1_CODE } from '../../../../../../../shared/mps-shared/domain/category/types.ts';
import { Input_Base } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Base.ts';
import { InputEditor } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/type.ts';

interface InputCategoryData {
  mpsCategories: CreatorProductDefaultData['mpsCategories'];
  sp_cate_root_code: z.infer<typeof categoryCodeSchema>;
  sp_cate_list_code: z.infer<typeof categoryCodeSchema>;
  sp_cate_item_code: z.infer<typeof categoryCodeSchema>;
  setField: (keyName: ValueOf<typeof formKeyMap>, value: string) => void;
  status?: keyof typeof FORM_DROPDOWN_CONSTANT;
}

const ROOT_CATE_LABEL = {
  [CategoryLevel1_CODE.GOODS]: '굿즈',
  [CategoryLevel1_CODE.HANDMADE]: '핸드메이드',
  [CategoryLevel1_CODE.BRAND_DEAL]: '브랜드딜',
};

export class Input_Category extends View<InputCategoryData> implements Input_Base<InputEditor> {
  private input_category_dropdown_status = this.data.status || FORM_DROPDOWN_CONSTANT.ACTIVE;

  private cate_root_dropdown_items = CategoryServiceS.findLevel1Categories(this.data.mpsCategories)
    .filter((category) => category.getCode() in ROOT_CATE_LABEL)
    .map((mpsCategory) => {
      return {
        key: mpsCategory.getCode(),
        name:
          ROOT_CATE_LABEL[mpsCategory.getCode() as Exclude<CategoryLevel1_CODE, 'EBOOK' | 'CURATION'>] ?? '',
        value: mpsCategory.getCode(),
      };
    });

  private cate_list_dropdown_items = this.data.sp_cate_root_code
    ? this.makeCateListDropDownItem(this.data.sp_cate_root_code)
    : [];

  private cate_item_dropdown_items = this.data.sp_cate_list_code
    ? this.makeCateItemDropDownItem(this.data.sp_cate_list_code, this.data.sp_cate_root_code)
    : [];

  private cateRootDropDown = new FormDropdown<string>(
    {
      name: formKeyMap.sp_cate_root_code,
      status: this.input_category_dropdown_status,
      items: this.cate_root_dropdown_items,
      selected_item: this.cate_root_dropdown_items.find(({ value }) => value === this.data.sp_cate_root_code),
      placeholder: '대 카테고리',
    },
    {
      klass: klass.dropdown,
      size: 'small',
      is_mobile: true,
      onSelectHook: (item) => {
        this.setCateList(item.value ?? '').setHidden(false);
        this.cateItemDropDown.setHidden(true);

        this.data.setField(formKeyMap.sp_cate_root_code, item.value ?? '');
        this.data.setField(formKeyMap.sp_cate_list_code, '');
        this.data.setField(formKeyMap.sp_cate_item_code, '');
      },
    },
  );

  private cateListDropDown = new FormDropdown<string>(
    {
      name: formKeyMap.sp_cate_list_code,
      status: this.input_category_dropdown_status,
      items: this.cate_list_dropdown_items,
      selected_item: this.cate_list_dropdown_items.find(({ value }) => value === this.data.sp_cate_list_code),
      placeholder: '중 카테고리',
    },
    {
      klass: klass.dropdown,
      size: 'small',
      is_mobile: true,
      onSelectHook: (item) => {
        this.setCateItem(item.value ?? '').setHidden(false);
        this.data.setField(formKeyMap.sp_cate_list_code, item.value ?? '');
        this.data.setField(formKeyMap.sp_cate_item_code, '');
      },
    },
  );

  private cateItemDropDown = new FormDropdown<string>(
    {
      name: formKeyMap.sp_cate_item_code,
      status: this.input_category_dropdown_status,
      items: this.cate_item_dropdown_items,
      selected_item: this.cate_item_dropdown_items.find(({ value }) => value === this.data.sp_cate_item_code),
      placeholder: '소 카테고리',
    },
    {
      klass: klass.dropdown,
      size: 'small',
      is_mobile: true,
      onSelectHook: (item) => {
        this.data.setField(formKeyMap.sp_cate_item_code, item.value ?? '');
      },
    },
  );

  override template() {
    return html`
      <div class="${base_klass.input_root}">
        <div class="${base_klass.title}">
          <span class="${shareFont('14_bold')}">카테고리</span>
          ${required_html}
        </div>
        <div class="${klass.dropdown_wrap}">
          ${this.cateRootDropDown} ${this.cateListDropDown} ${this.cateItemDropDown}
        </div>
        <span class="${base_klass.error_message} ${shareFont('12_medium')}"></span>
      </div>
    `;
  }

  override onRender() {
    this.cateListDropDown.setHidden(!this.data.sp_cate_root_code);
    this.cateItemDropDown.setHidden(!this.data.sp_cate_root_code);
  }

  private setCateList(cate_root_code: string) {
    return this.cateListDropDown.setItems(this.makeCateListDropDownItem(cate_root_code));
  }

  private setCateItem(code: string) {
    return this.cateItemDropDown.setItems(
      this.makeCateItemDropDownItem(code, this.cateRootDropDown.getSelectedItem().value),
    );
  }

  private makeCateListDropDownItem(cate_root_code: string) {
    return CategoryServiceS.findLevel2Categories(this.data.mpsCategories, cate_root_code).map(
      (mpsCategory) => {
        return {
          key: mpsCategory.getCode(),
          name: mpsCategory.getName() ?? '',
          value: mpsCategory.getCode(),
        };
      },
    );
  }

  private makeCateItemDropDownItem(
    cate_list_code: string,
    cate_root_code: string = CategoryLevel1_CODE.GOODS,
  ) {
    return CategoryServiceS.findLevel3Categories(this.data.mpsCategories, cate_list_code, cate_root_code).map(
      (mpsCategory) => {
        return {
          key: mpsCategory.getCode(),
          name: mpsCategory.getName() ?? '',
          value: mpsCategory.getCode(),
        };
      },
    );
  }

  public getCateRootDropDown() {
    return this.cateRootDropDown;
  }

  public getCateListDropDown() {
    return this.cateListDropDown;
  }

  public getCateItemDropDown() {
    return this.cateItemDropDown;
  }

  public getName() {
    return formKeyMap.sp_cate_root_code;
  }

  public getValue() {
    return undefined;
  }

  public setError(is_error: boolean, message?: string, name?: string) {
    if (name === formKeyMap.sp_cate_root_code) this.cateRootDropDown.setError(is_error);
    if (name === formKeyMap.sp_cate_list_code) this.cateListDropDown.setError(is_error);
    if (name === formKeyMap.sp_cate_item_code) this.cateItemDropDown.setError(is_error);

    controlErrorMessage(this.element(), message);
  }
}
